import * as THREE from 'three'
import Resources from '~/service/experience/utils/Resources'
import NidoPart, {
  NidoPartParameters,
} from '~/service/experience/world/nido/parts/NidoPart'
import {
  NidoCode,
  NidoId,
  NidoPosition,
  NidoSocketCode,
  NidoSocketId,
  NidoSocketType,
  NidoPartName,
} from '~/service/experience/world/nido/enum'
import { Nido } from '~/service/experience/world/nido/Nido'
import { NidoSeat } from '~/service/experience/world/nido/parts/NidoTexturePart'
import { NidoLowBackrest } from '~/service/experience/world/nido/parts/NidoTexturePart'
import NidoSocket from '~/service/experience/world/nido/parts/NidoSocket'

export default class NidoTable extends NidoPart {
  scene: THREE.Group
  resources: Resources
  parent: Nido
  lowBackrest: NidoLowBackrest | undefined
  socket: NidoSocket | undefined

  constructor(
    parent: Nido,
    scene: THREE.Group,
    resources: Resources,
    parameters: NidoPartParameters,
    lowBackrest?: NidoLowBackrest | any,
    socket?: NidoSocket | any
  ) {
    super(
      parent,
      scene,
      resources.models.table,
      parameters,
      NidoCode.TABLE,
      NidoId.TABLE
    )

    this.parent = parent
    this.scene = scene
    this.resources = resources

    if (lowBackrest) {
      if (lowBackrest instanceof NidoLowBackrest) {
        this.lowBackrest = lowBackrest
      } else {
        this.addLowBackrest(
          lowBackrest.isCorner,
          lowBackrest.isInner,
          lowBackrest.textureId,
          lowBackrest
        )
      }
    }

    if (socket) {
      if (socket instanceof NidoSocket) {
        this.socket = socket
      } else {
        this.addSocket(socket.isCorner)
      }
    }
  }

  switchToSeat(isCorner: boolean, isInner = false) {
    this.scene.remove(this.model)
    this.lowBackrest?.remove()

    this.parent.parts[this.position] = new NidoSeat(
      this.parent,
      this.scene,
      this.resources,
      94,
      { offset: this.offset, inverted: this.inverted, position: this.position },
      this.lowBackrest,
      this.socket
    )

    if (isCorner) {
      this.parent.parts[this.position]?.addLowBackrest(true, isInner)
    }
    const configurator = useConfigurator()
    configurator.addedChair()
  }

  addLowBackrest(
    isCorner: boolean,
    isInner = false,
    textureId = 94,
    lowBackrest?: any
  ) {
    this.lowBackrest = new NidoLowBackrest(
      this.parent,
      this.scene,
      this.resources,
      textureId,
      this.offset,
      this.inverted,
      isCorner,
      isInner,
      this.position,
      lowBackrest
    )

    const configurator = useConfigurator()
    configurator.addedChair()

    if (isCorner || isInner) {
      this.parent.toggleAddButton(this.position || NidoPosition.CENTER, false)
    }
  }

  removeLowBackrest() {
    if (!this.lowBackrest) return

    this.lowBackrest.remove()

    this.scene.remove(this.lowBackrest.model)
    this.lowBackrest = undefined

    const configurator = useConfigurator()
    configurator.addedChair()

    this.parent.toggleAddButton(this.position || NidoPosition.CENTER, true)
  }

  addSocket(
    isCorner: boolean,
    socketCode?: NidoSocketCode,
    socketId?: NidoSocketId
  ) {
    if (socketCode === undefined || socketId === undefined) {
      const world = useWorld()
      const defaultSocketType = world.socketType ?? NidoSocketType.SOCKET_CZ
      socketCode = NidoSocketCode[defaultSocketType]
      socketId = NidoSocketId[defaultSocketType]
    }
    this.socket = new NidoSocket(
      this.parent,
      this.scene,
      this.resources,
      {
        offset: this.offset,
        inverted: this.inverted,
        position: this.position,
      },
      isCorner,
      socketCode,
      socketId
    )

    const configurator = useConfigurator()
    configurator.addedChair()
  }

  removeSocket() {
    if (!this.socket) return

    this.scene.remove(this.socket.model)
    this.socket = undefined

    const configurator = useConfigurator()
    configurator.addedChair()
  }

  invert() {
    super.invert()

    this.lowBackrest?.invert()
    this.socket?.invert()
  }

  // TODO: Get all parts from Nido
  getParts(): NidoPart[] {
    return [this, this.lowBackrest?.getParts(), this.socket?.getParts()]
      .flat(Infinity)
      .filter((part) => part !== undefined) as NidoPart[]
  }

  toJSON() {
    const json = super.toJSON()

    const obj = JSON.parse(json)

    const lowBackrest = this.lowBackrest?.toJSON()
    const socket = this.socket?.toJSON()

    obj.type = NidoPartName.TABLE

    if (lowBackrest) {
      obj.lowBackrest = JSON.parse(lowBackrest)
    }

    if (socket) {
      obj.socket = JSON.parse(socket)
    }

    return JSON.stringify(obj, null, '\t')
  }
}
